<script setup lang="ts">
import { computed, ref } from "vue"
import { capitalize, isNil } from "lodash-es"
import { Severity } from "@tinyurldev/tu-ui-vue"

import TuPlanInformationCard from "@Components/plan-information-card/TuPlanInformationCard.vue"
import useTranslation from "@Composables/useTranslation"
import OrgCancellation from "@Modules/subscriptions/Views/feedbacks/OrgCancellation.vue"
import OrgPastDue from "@Modules/subscriptions/Views/feedbacks/OrgPastDue.vue"

import type { Subscription } from "@Modules/subscriptions/Data/Models/SubscriptionModel"
import type { ComponentProps } from "@Modules/shared/interfaces/ComponentProps"

//
// Setup
const { __ } = useTranslation()

//
// Props
interface Props {
  subscription: Subscription | null
  resumeSubscription: () => Promise<void>
  cancelDowngrade: () => Promise<void>
  loading: boolean
}
const props = defineProps<Props>()

//
// Data
const isFeedbackButtonLoading = ref(false)

//
// Computed
const planName = computed((): string => {
  if (isNil(props.subscription)) {
    return __("billing.free")
  }

  const name = props.subscription.name
  const interval = props.subscription.interval

  // Capitalize the name
  const formattedName = capitalize(name)

  if (interval) {
    const intervalLabel = interval === "yearly"
      ? __("pricing_page.annual_billing_frequency")
      : __("pricing_page.monthly_billing_frequency")
    return `${formattedName} ${intervalLabel}`
  }

  return formattedName
})

const planFeatures = computed((): Array<{ id: string; icon: string; label: string }> => {
  if (isNil(props.subscription)) {
    return [{ id: "free", icon: "fa-check", label: __("plan_information_card.free_plan_feature") }]
  }

  const features: Array<{ id: string; icon: string; label: string }> = []
  const options = props.subscription.options ?? {}
  const highlighted = props.subscription.highlighted ?? []

  // Build features from highlighted options
  for (const key of highlighted) {
    const value = options[key] ?? null
    const label = formatFeatureLabel(key, value)

    if (label) {
      features.push({
        id: key,
        icon: value !== null && value !== 0 ? "fa-check" : "fa-xmark",
        label,
      })
    }
  }

  return features.length > 0
    ? features
    : [{ id: "active", icon: "fa-check", label: __("plan_information_card.active_subscription") }]
})

const planPrice = computed((): string | null => {
  if (isNil(props.subscription) || props.subscription.isFree()) {
    return null
  }

  const amount = props.subscription.amount
  if (isNil(amount)) {
    return null
  }

  return `$${(amount / 100).toFixed(2)}`
})

const planInterval = computed((): string => {
  return props.subscription?.interval === "yearly"
    ? __("billing.interval.short.yearly")
    : __("billing.interval.short.monthly")
})

const renewalDate = computed((): string | null => {
  return props.subscription?.nextRenewalDate() ?? null
})

const isPastDue = computed((): boolean => {
  return props.subscription?.isPastDue() ?? false
})

const isBeingCancelled = computed((): boolean => {
  return !!props.subscription?.isOnGracePeriod();
})

const isBannerVisible = computed((): boolean => {
  return isPastDue.value || isBeingCancelled.value
})

const bannerProps = computed(() => {
  if (!isBannerVisible.value) {
    return null
  }

  if (isPastDue.value) {
    return {
      isFeedbackButtonLoading: isFeedbackButtonLoading.value,
      feedbackContent: OrgPastDue,
      feedbackButton: {
        label: __("pricing_page.button_label.update_payment_method"),
        severity: Severity.ALERT,
      },
      feedbackSeverity: Severity.ALERT,
    }
  }

  if (isBeingCancelled.value) {
    return {
      isFeedbackButtonLoading: isFeedbackButtonLoading.value,
      feedbackContent: OrgCancellation,
      feedbackContentProps: { endsAt: props.subscription?.ends_at ?? null },
      feedbackButton: {
        label: __("pricing_page.button_label.resume_subscription"),
        severity: Severity.SECONDARY,
      },
      feedbackSeverity: Severity.ALERT,
    }
  }

  return null
})

const isEnterprise = computed((): boolean => {
  return props.subscription?.isEnterprise() ?? false
})

const ctaButtonProps = computed(() => {
  if (!isEnterprise.value) {
    return null
  }

  return {
    isOutlined: true,
    severity: Severity.PRIMARY,
    label: __("contact_sales_to_update"),
  }
})

const planInformationProps = computed((): ComponentProps<typeof TuPlanInformationCard> => {
  // Vue props are readonly, so the object is built in one literal; optional props
  // are spread in only when present to preserve the "omit when null" behaviour.
  return {
    planName: planName.value,
    planFeatures: planFeatures.value,
    planInterval: planInterval.value,
    isBannerVisible: isBannerVisible.value,
    isLegacy: false,
    ...(planPrice.value !== null ? { planPrice: planPrice.value } : {}),
    ...(renewalDate.value !== null ? { renewalDate: renewalDate.value } : {}),
    ...(bannerProps.value ? { bannerProps: bannerProps.value } : {}),
    ...(ctaButtonProps.value ? { ctaButtonProps: ctaButtonProps.value } : {}),
  }
})

//
// Methods
function formatFeatureLabel(key: string, value: number | string | null): string {
  const translationKeys: Record<string, string> = {
    max_aliases_per_month: "pricing_page.find_plan_section.plan_facts.links_per_month",
    max_redirects_per_month: "pricing_page.find_plan_section.plan_facts.tracked_clicks",
    max_branded_domains: "pricing_page.find_plan_section.plan_facts.branded_domains",
    links_with_unlimited_clicks: "pricing_page.find_plan_section.plan_facts.links_per_month",
  }

  let amount: string
  if (value === null) {
    amount = __("unlimited")
  } else if (typeof value === "number") {
    amount = value.toLocaleString()
  } else {
    amount = value
  }

  const translationKey = translationKeys[key]
  if (translationKey) {
    return __(translationKey, { amount })
  }

  return `${amount} ${key.replace(/_/g, " ")}`
}

async function handleBannerClick(): Promise<void> {
  isFeedbackButtonLoading.value = true

  try {
    if (isBeingCancelled.value) {
      await props.resumeSubscription()
    }
    // Note: payment method update would need to be handled separately
  } finally {
    isFeedbackButtonLoading.value = false
  }
}

function handleBodyClick(): void {
  // For enterprise plans, this would redirect to sales
  // Using window.location for external link
  window.location.href = "/contact"
}

//
// Exposed Test Vars
defineExpose({
  planName,
  planFeatures,
  planPrice,
  planInterval,
  renewalDate,
  isBannerVisible,
})
</script>

<template>
  <TuPlanInformationCard
    class="_org_plan_information"
    v-bind="planInformationProps"
    @banner-click="handleBannerClick"
    @body-click="handleBodyClick"
  />
</template>
